import React, { ChangeEvent, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaText from "../../ui-kit/typography/Text/Text";

export interface IMergeBranchOption {
  value: string;
  label: string;
  /** `data-testid` for this branch's option row — branches are addressed by name, not by id. */
  testId?: string;
  /** Whether the current user may merge into this branch (undefined = fail open; server enforces). */
  canMergeInto?: boolean;
  isProtected?: boolean;
}

export interface IMergeSetupPanelProps {
  sourceBranchName?: string;
  branches: IMergeBranchOption[];
  inProgress?: boolean;
  onStart: (summary: string, description: string, targetBranch: string) => void;
  onCancel: () => void;
}

/**
 * Inline merge configuration — integrated into the merge tab's own frame (header
 * bar + body + footer), not a modal popping the moment the tab opens.
 */
const MergeSetupPanel: React.FC<IMergeSetupPanelProps> = ({
  sourceBranchName,
  branches,
  inProgress,
  onStart,
  onCancel
}) => {
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [targetBranch, setTargetBranch] = useState<string>("");

  const selectedTarget = branches.find((b) => b.value === targetBranch);
  const targetName = selectedTarget?.label;
  // A target the user can't merge into directly (e.g. master, without owner rights) now routes
  // through a merge request rather than being blocked — the completion becomes "Request merge".
  const targetRequiresRequest = !!selectedTarget && selectedTarget.canMergeInto === false;
  const canStart = !!summary.trim() && !!targetBranch && !inProgress;

  const handleBranchChange = (value: string | number | Array<string | number> | null) => {
    setTargetBranch(typeof value === "string" ? value : "");
  };

  return (
    <div className="branch-merge__setup">
      <div className="branch-merge__setup-bar">
        <div className="branch-merge__setup-bar-title">
          <KuikaSvg name="git-merge" width={16} height={16} />
          <h3>Merge into a branch</h3>
        </div>
        <div className="branch-merge__setup-flow">
          <span className="branch-merge__setup-branch branch-merge__setup-branch--source">
            <span className="branch-merge__setup-branch-dot" />
            {sourceBranchName || "Current branch"}
          </span>
          <KuikaSvg name="arrow-right" type="arrows" width={15} height={15} className="branch-merge__setup-arrow" />
          <span
            className="branch-merge__setup-branch branch-merge__setup-branch--target"
            data-empty={!targetBranch || undefined}
          >
            <span className="branch-merge__setup-branch-dot" />
            {targetName || "Select target"}
          </span>
        </div>
      </div>

      <div className="branch-merge__setup-body">
        <div className="branch-merge__setup-intro">
          <KuikaText>Choose a target branch and describe the merge before reviewing changes.</KuikaText>
        </div>
        <div className="branch-merge__setup-fields">
          <div className="branch-merge__setup-field">
            <label>Target branch</label>
            <KuikaSelectbox
              data-testid="mergeTargetBranchSelect"
              placeholder="Merge into"
              options={branches}
              value={targetBranch}
              onChange={handleBranchChange}
              allowClear
            />
            {targetRequiresRequest && (
              <div className="branch-merge__setup-blocked">
                <KuikaSvg name="git-merge" width={14} height={14} />
                <span>
                  “{targetName}” is protected. You’ll resolve any conflicts and then submit a merge request for the
                  branch owner to approve.
                </span>
              </div>
            )}
          </div>
          <div className="branch-merge__setup-field">
            <label>Summary</label>
            <KuikaInput
              data-testid="mergeSummaryInput"
              placeholder="What does this merge do?"
              value={summary}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSummary(e.target.value)}
              disabled={inProgress}
              autoFocus
            />
          </div>
          <div className="branch-merge__setup-field">
            <label>Description</label>
            <KuikaInput
              type="textarea"
              placeholder="Add more detail (optional)"
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDescription(e.target.value)}
              disabled={inProgress}
            />
          </div>
        </div>
      </div>

      <div className="branch-merge__setup-footer">
        <KuikaButton type="secondary" color="neutral" size="md" onClick={onCancel} disabled={inProgress}>
          Cancel
        </KuikaButton>
        <KuikaButton
          type="primary"
          color="default"
          size="md"
          onClick={() => onStart(summary, description, targetBranch)}
          disabled={!canStart}
          loading={inProgress}
          data-testid="mergeStartButton"
        >
          <span className="branch-merge__complete-label">
            <KuikaSvg name="git-merge" width={16} height={16} />
            Start merge
          </span>
        </KuikaButton>
      </div>
    </div>
  );
};

export default MergeSetupPanel;
